import {
  AllAttributesType,
  allAttrsEnum,
  MetricsType,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import { FormItemFilterRecord } from '@/services/api/resources/analytics-builder/model';
import { useAttributes } from './useAttributes';
import { WidgetRequestInput } from '@/ui/concerns/analytics/widget/useWidgets';
import { useAnalyticsMap } from '@/ui/concerns/analytics/useAnalyticsMap';
import { ResponsesTypes } from '@/entities/checklist';

export const useWidgetValidation = () => {
  const { getAllowedDisplaysByResponseType } = useAttributes();
  const {
    containsAttribute,
    isMetricOnlyRatioValues,
    isRatioValuesTypeEnabled,
    isAllChartsEnabled,
  } = useAnalyticsMap();

  const isRequestParamsValid = (params: WidgetRequestInput, formItemDetails: FormItemFilterRecord | undefined) => {
    if (!params.metric || !params.attribute || !params.display) {
      return false;
    }

    if (!isMetricRequestValid(params.metric, params.percentageToggled)) {
      return false;
    }

    if (!isAttributeRequestValid(params.attribute, params.display, params.percentageToggled, params.metric)) {
      return false;
    }

    if (params.attribute === allAttrsEnum.response
      && !isResponseAttributeRequestValid(params.display, formItemDetails, params.filters.formItem)) {
      return false;
    }

    return true;
  };

  const isMetricRequestValid = (metric: MetricsType, percentageToggled: boolean) =>
    !(!percentageToggled && isMetricOnlyRatioValues(metric));

  const isAttributeRequestValid = (
    attribute: AllAttributesType,
    display: WidgetDisplayOptions,
    percentageToggled: boolean,
    metric: MetricsType,
  ) => {
    if (!containsAttribute(metric, attribute)) {
      return false;
    }

    if (!isRatioValuesTypeEnabled(metric, attribute) && percentageToggled) {
      return false;
    }

    if (!isAllChartsEnabled(metric, attribute)
      && [WidgetDisplayOptions.lineChart, WidgetDisplayOptions.barChart].includes(display)) {
      return false;
    }

    return !isAllChartsEnabled(metric, attribute) || display !== WidgetDisplayOptions.singleNumber;
  };

  const isResponseAttributeRequestValid = (
    display: WidgetDisplayOptions,
    formItemDetails: FormItemFilterRecord | undefined,
    filterFormItemId: string | null | undefined,
  ): boolean => {
    if (!(filterFormItemId || formItemDetails)) {
      return false;
    }

    if (filterFormItemId !== formItemDetails?.id) {
      return false;
    }

    if (!formItemDetails?.type) {
      return false;
    }

    if (display === WidgetDisplayOptions.singleNumber) {
      return false;
    }

    if (display === WidgetDisplayOptions.table) {
      return true;
    }

    if (!getAllowedDisplaysByResponseType(formItemDetails?.type as ResponsesTypes).includes(display)) {
      return false;
    }

    return true;
  };

  const getDisplayByFormItem = (formItemDetails: FormItemFilterRecord | undefined) => {
    const displays = getAllowedDisplaysByResponseType(formItemDetails?.type as ResponsesTypes);

    if (displays.length === 0) {
      return WidgetDisplayOptions.table;
    }

    return displays[0];
  };

  return {
    isRequestParamsValid,
    isMetricRequestValid,
    isAttributeRequestValid,
    isResponseAttributeRequestValid,
    getDisplayByFormItem,
  };
};
